/**
 * 앱을 여는 host를 Firebase `authDomain`과 같게 고정한다.
 *
 * 소셜 로그인 redirect는 provider 인증을 마친 사용자를
 * `<authDomain>/__/auth/handler`로 돌려보낸다. 앱 host가 authDomain과 다르면
 * 3rd-party storage를 분리하는 브라우저가 handler의 저장소를 나눠, handler가
 * 왕복 전에 sessionStorage에 남긴 state를 되읽지 못하고
 * `missing initial state`로 끝난다.
 *
 * dev·stg는 Hosting이 `*.web.app`과 `*.firebaseapp.com`을 한 site에 함께
 * 붙여주므로 두 host 모두 같은 앱을 서빙한다. 그중 authDomain과 같은 쪽으로
 * 옮겨야 소셜 로그인이 성립한다.
 */

/** Firebase Hosting이 한 site에 기본으로 붙여주는 도메인. */
const FIREBASE_HOSTING_SUFFIXES = [".web.app", ".firebaseapp.com"] as const;

/** 로컬 개발 서버는 authDomain과 같을 수 없으므로 대상에서 제외한다. */
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

const isLocalHost = (host: string): boolean =>
  LOCAL_HOSTS.has(host) || host.endsWith(".localhost") || host.endsWith(".local");

const isFirebaseHostingHost = (host: string): boolean =>
  FIREBASE_HOSTING_SUFFIXES.some((suffix) => host.endsWith(suffix));

/**
 * 옮겨야 하면 목적지 host를, 그대로 둬야 하면 null을 돌려준다.
 *
 * Firebase 기본 도메인으로 들어온 접속만 옮긴다. 커스텀 도메인이나 알 수 없는
 * host까지 옮기면 authDomain 설정이 틀렸을 때 접근 자체를 막아버린다.
 */
export function resolveCanonicalHostRedirect({
  currentHost,
  authDomain,
}: {
  currentHost: string | null | undefined;
  authDomain: string | null | undefined;
}): string | null {
  const host = (currentHost ?? "").trim().toLowerCase();
  const canonical = (authDomain ?? "").trim().toLowerCase();

  if (!host || !canonical) return null;
  if (host === canonical) return null;
  if (isLocalHost(host)) return null;
  if (!isFirebaseHostingHost(host)) return null;

  return canonical;
}

/**
 * 정본 host로 한 번 옮긴다. path·query·hash는 그대로 가져간다.
 *
 * React가 그리기 전에 호출해야 사용자가 잘못된 host에서 화면을 보고 조작하는
 * 시간이 생기지 않는다. 옮겼으면 true.
 */
export function enforceCanonicalHost(): boolean {
  if (typeof window === "undefined") return false;

  const { location } = window;
  const target = resolveCanonicalHostRedirect({
    currentHost: location.hostname,
    authDomain: process.env.REACT_APP_authDomain,
  });
  if (!target) return false;

  const url = new URL(location.href);
  url.hostname = target;
  // 기본 포트 외의 포트가 남아 있으면 정본 host에서 열리지 않는다.
  url.port = "";
  // 되돌아오는 순환을 만들지 않도록 history에 남기지 않고 대체한다.
  location.replace(url.toString());
  return true;
}
